import React, { useState } from 'react';
import {
  Package,
  ArrowLeft,
  Search,
  Filter,
  Plus,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Truck,
  Scan,
  FileText,
  Calendar,
  User,
  Building,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Download,
  Edit
} from 'lucide-react';
import { receivingRecords, receivingDashboard } from '../../data/receivingData';

const ReceivingManagement = ({ onBack, onSelectReceiving, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedReceiving, setSelectedReceiving] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  // Filter receiving records
  const filteredRecords = receivingRecords.filter(record => {
    const matchesSearch = record.receivingNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         record.supplierName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         record.purchaseOrderId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || record.status === statusFilter;
    // Date filter logic would go here
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'pending_inspection': return 'text-yellow-600 bg-yellow-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return '已完成';
      case 'pending_inspection': return '待檢驗';
      case 'rejected': return '已拒收';
      case 'in_progress': return '進行中';
      default: return '未知';
    }
  };

  const handleReceivingSelect = (record) => {
    setSelectedReceiving(record);
    setActiveView('detail');
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">收貨管理</h1>
          <p className="text-gray-600 mt-1">管理貨物接收、檢驗和入庫</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setActiveView('create')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新增收貨記錄</span>
          </button>
          <button
            onClick={() => setActiveView('list')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Eye className="w-4 h-4" />
            <span>查看所有記錄</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">總收貨記錄</p>
              <p className="text-2xl font-bold text-gray-900">{receivingDashboard.summary.totalReceipts}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-sm text-green-600">+8 本月新增</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">待處理收貨</p>
              <p className="text-2xl font-bold text-gray-900">{receivingDashboard.summary.pendingReceipts}</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <span className="text-sm text-gray-600">需要處理</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">品質通過率</p>
              <p className="text-2xl font-bold text-gray-900">{receivingDashboard.summary.qualityPassRate}%</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-sm text-green-600">+1.2% 比上月</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">平均處理時間</p>
              <p className="text-2xl font-bold text-gray-900">{receivingDashboard.summary.averageProcessingTime}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingDown className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-sm text-green-600">-0.3天 比上月</span>
          </div>
        </div>
      </div>

      {/* Recent Receipts */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">最新收貨記錄</h2>
          <button
            onClick={() => setActiveView('list')}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            查看全部
          </button>
        </div>

        <div className="space-y-4">
          {receivingDashboard.recentReceipts.map((receipt, index) => (
            <div key={receipt.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Package className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{receipt.id}</h3>
                    <p className="text-sm text-gray-600">{receipt.supplier}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="text-sm text-gray-600">收貨金額</p>
                    <p className="font-semibold text-gray-900">HK${receipt.value.toLocaleString()}</p>
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(receipt.status)}`}>
                    {getStatusText(receipt.status)}
                  </span>
                  <button
                    onClick={() => handleReceivingSelect(receivingRecords.find(r => r.id === receipt.id))}
                    className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                  >
                    查看
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>收貨日期: {receipt.date}</span>
                <span>PO: {receivingRecords.find(r => r.id === receipt.id)?.purchaseOrderId}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pending Inspections */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">待檢驗項目</h2>
          <span className="bg-yellow-100 text-yellow-800 text-sm px-2 py-1 rounded-full">
            {receivingDashboard.pendingInspections.length} 項待處理
          </span>
        </div>

        <div className="space-y-3">
          {receivingDashboard.pendingInspections.map((inspection, index) => (
            <div key={inspection.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center space-x-3">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                <div>
                  <p className="font-medium text-gray-900">{inspection.product}</p>
                  <p className="text-sm text-gray-600">{inspection.supplier}</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <p className="text-sm text-gray-600">預期完成</p>
                  <p className="font-medium text-gray-900">{inspection.dueDate}</p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  inspection.priority === 'high' ? 'bg-red-100 text-red-800' :
                  inspection.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {inspection.priority === 'high' ? '高' : inspection.priority === 'medium' ? '中' : '低'}優先級
                </span>
                <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                  開始檢驗
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quality Metrics Chart */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">品質趨勢分析</h2>
        <div className="space-y-4">
          {receivingDashboard.qualityMetrics.map((metric, index) => (
            <div key={metric.month} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-900 w-16">{metric.month}</span>
                <div className="flex space-x-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-gray-600">通過率</span>
                    <span className="text-sm font-medium">{metric.passRate}%</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span className="text-sm text-gray-600">缺陷率</span>
                    <span className="text-sm font-medium">{metric.defectRate}%</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <span className="text-sm text-gray-600">拒收率</span>
                    <span className="text-sm font-medium">{metric.rejectionRate}%</span>
                  </div>
                </div>
              </div>
              <div className="flex space-x-2">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full"
                    style={{ width: `${metric.passRate}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Rejection Reasons */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">主要拒收原因</h2>
        <div className="space-y-4">
          {receivingDashboard.topRejectionReasons.map((reason, index) => (
            <div key={reason.reason} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-red-600">{index + 1}</span>
                </div>
                <span className="font-medium text-gray-900">{reason.reason}</span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-24 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-red-500 h-2 rounded-full"
                    style={{ width: `${reason.percentage}%` }}
                  ></div>
                </div>
                <span className="text-sm text-gray-600 w-12">{reason.percentage}%</span>
                <span className="text-sm font-medium text-gray-900 w-8">{reason.count}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderReceivingList = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">收貨記錄列表</h1>
          <p className="text-gray-600 mt-1">查看和管理所有收貨記錄</p>
        </div>
        <button
          onClick={() => setActiveView('dashboard')}
          className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>返回總覽</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">搜尋</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜尋收貨編號或供應商"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">狀態</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">全部狀態</option>
              <option value="completed">已完成</option>
              <option value="pending_inspection">待檢驗</option>
              <option value="rejected">已拒收</option>
              <option value="in_progress">進行中</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">日期範圍</label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">全部日期</option>
              <option value="today">今天</option>
              <option value="week">本週</option>
              <option value="month">本月</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setDateFilter('all');
              }}
              className="w-full bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
            >
              重置篩選
            </button>
          </div>
        </div>
      </div>

      {/* Receiving Records Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  收貨編號
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  採購訂單
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  供應商
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  收貨日期
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  收貨人員
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  總金額
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  狀態
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRecords.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {record.receivingNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {record.purchaseOrderId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {record.supplierName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {record.receivingDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {record.receivedBy}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    HK${record.totalValue.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(record.status)}`}>
                      {getStatusText(record.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleReceivingSelect(record)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      查看
                    </button>
                    <button className="text-gray-600 hover:text-gray-900">
                      編輯
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderReceivingDetail = () => {
    if (!selectedReceiving) return null;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{selectedReceiving.receivingNumber}</h1>
            <p className="text-gray-600 mt-1">收貨記錄詳情</p>
          </div>
          <button
            onClick={() => setActiveView('list')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回列表</span>
          </button>
        </div>

        {/* Basic Information */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">基本資訊</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">收貨編號</label>
              <p className="text-gray-900">{selectedReceiving.receivingNumber}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">採購訂單</label>
              <p className="text-gray-900">{selectedReceiving.purchaseOrderId}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">供應商</label>
              <p className="text-gray-900">{selectedReceiving.supplierName}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">收貨日期</label>
              <p className="text-gray-900">{selectedReceiving.receivingDate}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">收貨人員</label>
              <p className="text-gray-900">{selectedReceiving.receivedBy}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">狀態</label>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedReceiving.status)}`}>
                {getStatusText(selectedReceiving.status)}
              </span>
            </div>
          </div>
        </div>

        {/* Delivery Information */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">配送資訊</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">送貨單號</label>
              <p className="text-gray-900">{selectedReceiving.deliveryInfo.deliveryNote}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">承運商</label>
              <p className="text-gray-900">{selectedReceiving.deliveryInfo.carrier}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">追蹤號碼</label>
              <p className="text-gray-900">{selectedReceiving.deliveryInfo.trackingNumber}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">送貨日期</label>
              <p className="text-gray-900">{selectedReceiving.deliveryInfo.deliveryDate}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">送貨時間</label>
              <p className="text-gray-900">{selectedReceiving.deliveryInfo.deliveryTime}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">司機</label>
              <p className="text-gray-900">{selectedReceiving.deliveryInfo.driverName}</p>
            </div>
          </div>
        </div>

        {/* Received Items */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">收貨明細</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    產品代碼
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    產品名稱
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    訂購數量
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    收貨數量
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    接受數量
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    拒收數量
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    狀態
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {selectedReceiving.items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.productCode}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.productName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.orderedQuantity.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.receivedQuantity.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {item.acceptedQuantity.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                      {item.rejectedQuantity.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.condition}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quality Inspection Status */}
        {selectedReceiving.qualityInspection.required && (
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">品質檢驗狀態</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">檢驗編號</label>
                <p className="text-gray-900">{selectedReceiving.qualityInspection.inspectionId}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">檢驗員</label>
                <p className="text-gray-900">{selectedReceiving.qualityInspection.inspector}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">檢驗日期</label>
                <p className="text-gray-900">{selectedReceiving.qualityInspection.inspectionDate || '待安排'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">檢驗結果</label>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  selectedReceiving.qualityInspection.result === 'passed_with_conditions' ? 'text-yellow-600 bg-yellow-100' :
                  selectedReceiving.qualityInspection.result === 'passed' ? 'text-green-600 bg-green-100' :
                  selectedReceiving.qualityInspection.result === 'failed' ? 'text-red-600 bg-red-100' :
                  'text-gray-600 bg-gray-100'
                }`}>
                  {selectedReceiving.qualityInspection.result === 'passed_with_conditions' ? '有條件通過' :
                   selectedReceiving.qualityInspection.result === 'passed' ? '通過' :
                   selectedReceiving.qualityInspection.result === 'failed' ? '不通過' : '待檢驗'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Discrepancies */}
        {selectedReceiving.discrepancies.length > 0 && (
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">差異記錄</h2>
            <div className="space-y-3">
              {selectedReceiving.discrepancies.map((discrepancy, index) => (
                <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-medium text-red-800">{discrepancy.type}</h4>
                      <p className="text-red-700 text-sm mt-1">{discrepancy.description}</p>
                      <div className="mt-2 text-sm">
                        <span className="text-red-600">處理方式:</span>
                        <span className="text-red-700 ml-1">{discrepancy.action}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-red-600">負責部門:</span>
                        <span className="text-red-700 ml-1">{discrepancy.responsible}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'list':
        return renderReceivingList();
      case 'detail':
        return renderReceivingDetail();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="flex items-center space-x-4">
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>返回採購管理</span>
        </button>
      </div>

      {renderContent()}
    </div>
  );
};

export default ReceivingManagement; 